import { useTranslation } from "react-i18next"
import { z } from "zod"

const postalCodeRegex = /^[0-9]{5}$/
const passwordRules = {
  length: 8,
  upper: /[A-Z]/,
  lower: /[a-z]/,
  number: /\d/,
  special: /[\W_]/,
}
export const emailFormInitialValues = {
  email: "",
}
export const useValidationSchemas = () => {
  const { t } = useTranslation()

  const emailSchema = z.object({
    email: z.string(),
  })

  const emailLoginSchema = z.object({
    email: z.string(),
    password: z.string().min(1, t("password_required")),
  })

  const phoneLoginSchema = z.object({
    countryCode: z.string().regex(/^\+\d+$/),
    phoneNumber: z
      .string()
      .regex(/^(\+|\d{1,4}|\d{1,4})\s?\d{7,14}$/, {
        message: t("wrong_format"),
      })
      .min(8, t("phone_min"))
      .max(10, t("phone_max")),
  })

  const userDetailsSchema = z.object({
    street: z.string().min(2, t("street_required")),
    city: z.string().min(2, t("city_required")),
    // postalCode: z.string().min(2, t("postal_code_required")),
  })

  const newPasswordSchema = z
    .string()
    .regex(passwordRules.lower, t("password_include_lowercase"))
    .regex(passwordRules.number, t("password_include_number"))
    .min(
      passwordRules.length,
      `${t("password_at_least")} ${passwordRules.length} ${t(
        "characters_long",
      )}}`,
    )

  const resetPasswordInitialValues = {
    password: "",
    confirmPassword: "",
  }
  const resetPasswordSchema = z
    .object({
      password: newPasswordSchema,
      confirmPassword: newPasswordSchema,
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("passwords_do_not_match"),
      path: ["confirmPassword"],
    })

  const changePasswordInitialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  }
  const changePasswordSchema = z
    .object({
      currentPassword: z
        .string()
        .min(passwordRules.length, t("current_password_required"))
        .min(
          passwordRules.length,
          `${t("password_at_least")} ${passwordRules.length} ${t(
            "characters_long",
          )}`,
        ),
      newPassword: newPasswordSchema,
      confirmPassword: newPasswordSchema,
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t("passwords_not_match"),
      path: ["confirmPassword"],
    })

  const driverRegistrationSchema = z
    .object({
      city: z.string().min(2, t("city_required")),
      street: z.string().min(2, t("street_required")),
      postalCode: z
        .string()
        .regex(postalCodeRegex, t("invalid_postal_code"))
        .min(4, t("postal_code_min"))
        .max(10, t("postal_code_max")),
      pinCode: z
        .string()
        .min(4, t("pin_code_digits"))
        .max(4, t("pin_code_digits")),
      password: newPasswordSchema,
      confirmPassword: newPasswordSchema,
      under23AndInexperienced: z.boolean(),
      agreesToPrivacyPolicy: z
        .boolean()
        .refine((val) => val === true, t("agree_privacy_policy")),
      acknowledgesTerms: z
        .boolean()
        .refine((val) => val === true, t("agree_terms")),
      wantsSpecialOffers: z.boolean(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("passwords_not_match"),
      path: ["confirmPassword"],
    })

  const driverDetailsSchema = z.object({
    firstName: z.string().min(2, { message: t("input_first_name_required") }),
    lastName: z.string().min(2, { message: t("input_last_name_required") }),
    phone: z
    .string()
    .regex(/^\+?[1-9]\d{1,14}$/, { message: t("wrong_format") }) // E.164 format
    .min(10, t("phone_min")) // Minimum length with country code
    .max(15, t("phone_max")), // Maximum length with country code
  })

  const pinCodeSchema = z
    .string()
    .length(4, "Pin Code must be exactly 4 digits")
    .regex(/^\d+$/, "Pin Code must only contain numbers")

  const pinSchema = z.object({
    pin: pinCodeSchema,
  })
  const changePinCodeSchema = z
    .object({
      currentPin: pinCodeSchema,
      newPin: pinCodeSchema,
      confirmNewPin: pinCodeSchema,
    })
    .refine(
      (data) => {
        return data.newPin === data.confirmNewPin
      },
      {
        message: t("pin_not_match"),
        path: ["confirmNewPin"],
      },
    )

  const expiryDateSchema = z
    .string()
    .min(1, "Expiry date is required")
    .regex(
      /^(0[1-9]|1[0-2])\/?([0-9]{4}|[0-9]{2})$/,
      "Expiry date is not valid",
    )
    .transform((date) => (date.length === 5 ? date + "20" : date)) // Append "20" for two-digit years
    .refine((date) => {
      const [month, year] = date.split("/")
      const expiry = new Date(`${year}-${month}-01`)
      return expiry > new Date()
    }, "Card has expired")

  const cvvSchema = z
    .string()
    .min(1, "CVV is required")
    .length(3, "CVV must be 3 digits")
    .or(z.string().length(4, "CVV must be 4 digits"))

  const creditCardSchema = z.object({
    holderName: z.string().min(1, "Holder name is required"),
    cardNumber: z
      .string()
      .min(1, "Credit card number is required")
      .regex(/^\d{4}( \d{4}){3}$/),
    expiryDate: expiryDateSchema,
    cvv: cvvSchema,
  })

  return {
    emailSchema,
    emailLoginSchema,
    phoneLoginSchema,
    userDetailsSchema,
    newPasswordSchema,
    resetPasswordInitialValues,
    resetPasswordSchema,
    changePasswordInitialValues,
    changePasswordSchema,
    driverRegistrationSchema,
    driverDetailsSchema,
    pinSchema,
    changePinCodeSchema,
    expiryDateSchema,
    cvvSchema,
    creditCardSchema,
  }
}

const validateCardNumber = (number: string) => {
  const arr = (number + "").replace(/\D/g, "").split("").reverse()
  let sum = 0
  for (let i = 0; i < arr.length; i++) {
    let n = parseInt(arr[i])
    if (i % 2 !== 0) {
      n *= 2
      if (n > 9) {
        n -= 9
      }
    }
    sum += n
  }
  return sum % 10 === 0
}
